<script lang="ts">
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "$lib/components/ui/dialog"
  import DialogContent from "$lib/components/ui/dialog/dialog-content.svelte"
  import Dialog from "$lib/components/ui/dialog/dialog.svelte"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import type { _response } from "$lib/data/type/_response"
  import { sleep } from "$lib/sleep"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"

  const FormSchema = z.object({
    email: z.string().default(""),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  const FormValidate = FormSchema
    .required()
    .extend({
      email: z.email("Email tidak valid"),
    })

  let { open, setOpen }: {
    open: boolean
    setOpen: (open: boolean) => void
  } = $props()

  const form = createForm(() => ({
    defaultValues: FormEmpty,
    validators: {
      onSubmit: FormValidate
    },
    onSubmit: async ({ value }) => mutation.mutate(value),
  }))

  createQuery(() => ({
    enabled: open,
    queryKey: ["auth/login/form-password-forgot"],
    queryFn: async () => {

      await sleep(1)
      form.reset(FormEmpty)

      return ""
    }
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const response = await httpClient.post<_response<string>>("/api/auth/password-forgot", data)
      const responseData = response.data!

      form.reset(form.state.values)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (prev) => ({
          ...prev,
          errorMap: { onSubmit: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) {
        return
      }

      toast.success(responseData.message, {
        duration: 1000 * 10,
      })

      setOpen(false)
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message
      })
    }
  }))

</script>
<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="relative w-xs"
    onOpenAutoFocus={(e) => e.preventDefault()}
    onInteractOutside={(e) => e.preventDefault()}>
    <DialogHeader>
      <DialogTitle>Lupa Password</DialogTitle>
      <DialogDescription></DialogDescription>
    </DialogHeader>
    <form
      action="#"
      autocomplete="off"
      class="min-w-0 space-y-4"
      onsubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}>
      <form.Field
        name="email">
        {#snippet children(field)}
          <div>
            <FieldLabel>Email</FieldLabel>
            <Input
              type="email"
              name={field.name}
              autocapitalize="off"
              placeholder="email"
              value={field.state.value}
              oninput={(e) => field.handleChange(e.currentTarget.value)}
            />
            <FieldMessage name={field.name} {form}/>
          </div>
        {/snippet}
      </form.Field>
      <div class="py-2"></div>
      <DialogFooter>
        <div class="flex-1"></div>
        <Button
          variant="outline"
          type="button"
          onclick={() => setOpen(false)}
          disabled={mutation.isPending}>
          Batal
        </Button>
        <Button
          type="submit"
          class="group"
          data-loading={mutation.isPending}
          disabled={mutation.isPending}>
          <span class="group-data-[loading=true]:invisible">Kirim</span>
          <LoaderCircleIcon class="group-data-[loading=true]:visible invisible absolute animate-spin"/>
        </Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
